/** Кластеры складов: правила «подстроки имени склада → кластер» (порт секции tech-вкладки,
 * стр. 8154-8165). mp=all действует на все три МП (разворот — на клиенте при матчинге). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractErrorMessage } from "@/lib/api-errors";

const MP_LABEL: Record<string, string> = { all: "Все МП", ozon: "Ozon", wb: "WB", ym: "ЯМ" };

export function ClustersEditor({ canEdit }: { canEdit: boolean }): React.ReactElement {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["orders", "refs"] });
  const q = useQuery({ queryKey: ["orders", "refs", "clusters"], queryFn: api.listClusters });

  const upd = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<Omit<api.WarehouseClusterRule, "id">>) =>
      api.updateCluster(id, body),
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить кластер")),
  });
  const del = useMutation({
    mutationFn: api.deleteCluster,
    onSuccess: invalidate,
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось удалить")),
  });
  const create = useMutation({
    mutationFn: api.createCluster,
    onSuccess: () => {
      invalidate();
      setNewName("");
      setNewPatterns("");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось добавить кластер")),
  });

  const [newMp, setNewMp] = useState("all");
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#5ec8f2");
  const [newPatterns, setNewPatterns] = useState("");

  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="text-sm font-medium">Кластеры складов</div>
      <div className="text-[11px] text-muted-foreground">
        Ключи — подстроки имени склада через запятую (регистр не важен, ё=е). Первый совпавший
        по порядку кластер выигрывает.
      </div>
      <div className="space-y-1.5">
        {(q.data ?? []).map((c) => (
          <div key={c.id} className="flex items-center gap-2 text-xs">
            <select
              className="h-7 w-20 shrink-0 rounded border bg-background px-1 text-xs"
              disabled={!canEdit}
              value={c.mp}
              onChange={(e) => upd.mutate({ id: c.id, mp: e.target.value as api.WarehouseClusterRule["mp"] })}
            >
              {Object.entries(MP_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
            <BlurInput
              className="w-40 shrink-0"
              value={c.name}
              disabled={!canEdit}
              onCommit={(name) => upd.mutate({ id: c.id, name })}
            />
            <input
              type="color"
              value={c.color}
              disabled={!canEdit}
              onChange={(e) => upd.mutate({ id: c.id, color: e.target.value })}
              className="size-7 shrink-0 cursor-pointer rounded border"
            />
            <BlurInput
              className="flex-1"
              value={c.patterns}
              disabled={!canEdit}
              onCommit={(patterns) => upd.mutate({ id: c.id, patterns })}
            />
            {canEdit && (
              <button
                type="button"
                className="shrink-0 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                onClick={() => del.mutate(c.id)}
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex items-center gap-2 border-t pt-2">
          <select
            className="h-8 w-20 shrink-0 rounded border bg-background px-1 text-xs"
            value={newMp}
            onChange={(e) => setNewMp(e.target.value)}
          >
            {Object.entries(MP_LABEL).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Название"
            className="h-8 w-40"
          />
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="size-8 shrink-0 cursor-pointer rounded border"
          />
          <Input
            value={newPatterns}
            onChange={(e) => setNewPatterns(e.target.value)}
            placeholder="ключи, через, запятую"
            className="h-8 flex-1"
          />
          <Button
            size="sm"
            disabled={!newName.trim() || !newPatterns.trim()}
            onClick={() =>
              create.mutate({ mp: newMp, name: newName.trim(), color: newColor, patterns: newPatterns.trim() })
            }
          >
            Добавить
          </Button>
        </div>
      )}
    </div>
  );
}

function BlurInput({
  value,
  disabled,
  onCommit,
  className,
}: {
  value: string;
  disabled: boolean;
  onCommit: (value: string) => void;
  className?: string;
}) {
  const [v, setV] = useState(value);
  React.useEffect(() => setV(value), [value]);
  return (
    <input
      className={`h-7 rounded border bg-background px-1.5 text-xs focus:border-primary focus:outline-none ${className ?? ""}`}
      disabled={disabled}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        const t = v.trim();
        if (t && t !== value) onCommit(t);
        else setV(value);
      }}
    />
  );
}
